import camelcaseKeys from 'camelcase-keys';
import PipelineAPI from 'dashboard/api/pipelines';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const camelizePipeline = data => camelcaseKeys(data || {}, { deep: true });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || meta?.count || 0),
  page: Number(meta?.page || meta?.current_page || 1),
});

export const usePipelinesStore = createStore({
  name: 'pipelines',
  type: 'pinia',
  API: PipelineAPI,
  state: () => ({
    activePipelineId: null,
  }),

  getters: {
    getPipelinesList: state => state.records,
    getActivePipeline: state =>
      state.records.find(p => p.id === state.activePipelineId) || {},
  },

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    setActivePipelineId(pipelineId) {
      this.activePipelineId = Number(pipelineId);
    },

    upsertPipelineRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const response = await PipelineAPI.get(params);
        const payload = response.data.payload || response.data;
        const pipelines = (Array.isArray(payload) ? payload : [payload]).map(
          p => camelizePipeline(p)
        );
        this.records = pipelines;
        if (response.data.meta) this.setMeta(response.data.meta);
        return pipelines;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      this.setActivePipelineId(id);
      try {
        const response = await PipelineAPI.show(id);
        const pipeline = camelizePipeline(response.data.payload);
        this.upsertPipelineRecord(pipeline);
        return pipeline;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async create(pipeline) {
      this.setUIFlag({ creatingItem: true });
      try {
        const response = await PipelineAPI.create(pipeline);
        const newPipeline = camelizePipeline(response.data.payload);
        this.upsertPipelineRecord(newPipeline);
        return newPipeline;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...pipeline }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const response = await PipelineAPI.update(id, pipeline);
        const updated = camelizePipeline(response.data.payload);
        this.upsertPipelineRecord(updated);
        return updated;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await PipelineAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async reorderStages(pipelineId, stageIds) {
      this.setUIFlag({ reorderingStages: true });
      try {
        const response = await PipelineAPI.reorderStages(pipelineId, stageIds);
        const updatedStages = camelizePipeline(response.data.payload);
        const pipeline = this.getRecord(pipelineId);
        if (pipeline.id) {
          this.upsertPipelineRecord({ ...pipeline, stages: updatedStages });
        }
        return updatedStages;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ reorderingStages: false });
      }
    },
  }),
});
